/**
 * 格式化工具。与产品门户原型 page.tsx 的同名函数保持一致；原型只有中文，这里按语言区分：
 * 「万」「天」「时」只在 zh 下用，en / es 走千分位与 d / :00。组件里通过 lib/useFmt.ts 取当前语言的版本。
 */

export type Lang = 'zh' | 'en' | 'es';

const NUM_LOCALE: Record<Lang, string> = { zh: 'zh-CN', en: 'en-US', es: 'es-ES' };

/** i18n 的语言码（可能是 zh-CN / en-US）→ 本页支持的三种语言，未知的按 en（与 fallbackLng 一致） */
export function toLang(code: string | undefined): Lang {
    const l = (code ?? '').toLowerCase().split('-')[0];
    return l === 'zh' || l === 'es' ? l : 'en';
}

export function fmtNum(v: number, lang: Lang = 'zh'): string {
    return v.toLocaleString(NUM_LOCALE[lang]);
}

/** 包裹量大，zh 下 ≥1万 用「x.xx万」 */
export function fmtPcs(v: number, lang: Lang = 'zh'): string {
    return lang === 'zh' && v >= 10000 ? `${(v / 10000).toFixed(2)}万` : fmtNum(v, lang);
}

/** 最长停留：只显示小时 */
export function fmtDwell(h: number): string {
    return h < 10 ? `${h.toFixed(1)}h` : `${Math.round(h)}h`;
}

export function fmtHours(h: number, lang: Lang = 'zh'): string {
    if (h >= 48) return lang === 'zh' ? `${(h / 24).toFixed(1)}天` : `${(h / 24).toFixed(1)}d`;
    return `${h.toFixed(1)}h`;
}

/** 按查看者浏览器时区展示到秒 */
export function fmtSecLocal(iso: string): string {
    return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }).format(new Date(iso));
}

/** 2026-10-16 16:18 (GMT+8) —— 按查看者浏览器时区 */
export function fmtDateTimeLocal(iso: string): string {
    const d = new Date(iso);
    const datePart = new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
        .format(d)
        .replace(/\//g, '-');
    const timePart = new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    const zonePart =
        new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(d).find((p) => p.type === 'timeZoneName')
            ?.value ?? '';
    return `${datePart} ${timePart} (${zonePart})`;
}

/**
 * hub 当地整点标签，格式与数仓 to_char(..., 'yyyy-mm-dd hh') 一致。
 *
 * 原型里这个函数写死了 America/Los_Angeles（只开了 LAX 仓所以看不出问题）。仓内看板的既有约定是
 * 按 hub 当地时区切分小时，这里改成由调用方传入。
 * todo hetianyang 时区应从 hub 配置读取，不能靠 HUB_ZONE 这张前端静态表；等后端下发 hub 时区后替换。
 */
export function hubHourKey(ms: number, timeZone: string): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(new Date(ms));
    const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${g('year')}-${g('month')}-${g('day')} ${g('hour')}`;
}

/** '2026-10-16 14' → zh '10-16 14时'，en / es '10-16 14:00' */
export function hourText(l: string, lang: Lang = 'zh'): string {
    if (!l) return '';
    return lang === 'zh' ? `${l.slice(5, 10)} ${l.slice(11)}时` : `${l.slice(5, 10)} ${l.slice(11)}:00`;
}

export function hoursSince(iso: string, now: number): number {
    return Math.max(0, (now - new Date(iso).getTime()) / 3_600_000);
}
